/**
 * Moments-story — cerimônias das funções de colaboração e ofício:
 * convite (duas penas) · companhia ao vivo (duas velas) · versão restaurada
 * (ampulheta) · página limpa (comentários zerados) · meta cravada (flecha de
 * brasa) · batismo do escritor (novo título) · batismo do livro.
 * Mesma gramática do hub: material temático, molas, narrativa curta.
 */
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { Scene, SceneText, useAutoDone, BornCandle } from './moments';

/* ─────────── duas penas: convite de colaboração enviado ─────────── */
export function TwoQuillsMoment({ email, onDone }: { email: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4400);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative h-[110px] w-[160px]">
          {/* as penas entram de lados opostos e se cruzam */}
          <motion.span
            aria-hidden
            initial={{ x: -70, y: 30, rotate: -40, opacity: 0 }}
            animate={{ x: 0, y: 0, rotate: -24, opacity: 1 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 150, damping: 15 }}
            className="absolute left-[28px] top-[18px] text-[44px]"
            style={{ color: 'var(--ember-hot)', filter: 'drop-shadow(0 0 12px rgba(246,233,200,0.6))' }}
          >
            🪶
          </motion.span>
          <motion.span
            aria-hidden
            initial={{ x: 70, y: 30, rotate: 40, opacity: 0 }}
            animate={{ x: 0, y: 0, rotate: 24, opacity: 1 }}
            transition={{ delay: 0.45, type: 'spring', stiffness: 150, damping: 15 }}
            className="absolute right-[28px] top-[18px] text-[44px]"
            style={{ color: '#A8B8E8', filter: 'drop-shadow(0 0 12px rgba(168,184,232,0.6)) scaleX(-1)', transform: 'scaleX(-1)' }}
          >
            🪶
          </motion.span>
          {/* o laço de tinta amarra as duas */}
          <svg viewBox="0 0 160 110" className="absolute inset-0 h-full w-full" aria-hidden>
            <motion.path
              d="M40 88 C 60 100, 100 100, 120 88"
              fill="none"
              stroke="rgba(223,201,147,0.6)"
              strokeWidth="1.5"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 1.0, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            />
          </svg>
        </div>
        <SceneText
          delay={1.4}
          warm
          eyebrow={t('hud.twoquillsEyebrow', 'convite enviado')}
          title={t('hud.twoquillsTitle', 'Uma pena a mais para esta história.')}
          sub={email}
        />
      </div>
    </Scene>
  );
}

/* ─────────── companhia: alguém escreve com você AO VIVO ─────────── */
export function CompanionMoment({ name, onDone }: { name: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4400);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <motion.div
          initial={{ y: 24, opacity: 0, scale: 2.2 }}
          animate={{ y: 0, opacity: 1, scale: 2.2 }}
          transition={{ type: 'spring', stiffness: 140, damping: 15 }}
          className="flex items-end gap-5"
          style={{ transformOrigin: 'center bottom' }}
        >
          <BornCandle delay={0.5} />
          <BornCandle delay={0.9} />
        </motion.div>
        <SceneText
          delay={1.3}
          warm
          eyebrow={t('hud.companionEyebrow', 'não estás só')}
          title={t('hud.companionTitle', 'Duas chamas na mesma página.')}
          sub={name}
        />
      </div>
    </Scene>
  );
}

/* ─────────── a ampulheta: versão restaurada ─────────── */
export function TimeBackMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4400);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <motion.div
          initial={{ rotate: 0, opacity: 0, y: 18 }}
          animate={{ rotate: 180, opacity: 1, y: 0 }}
          transition={{ delay: 0.25, type: 'spring', stiffness: 90, damping: 13 }}
          className="relative h-[104px] w-[72px]"
        >
          {/* moldura da ampulheta */}
          <span className="absolute inset-x-0 top-0 h-[8px] rounded-full" style={{ background: 'linear-gradient(180deg,#8B9AC8,#4E5B85)' }} />
          <span className="absolute inset-x-0 bottom-0 h-[8px] rounded-full" style={{ background: 'linear-gradient(180deg,#8B9AC8,#4E5B85)' }} />
          {/* os dois bulbos de vidro */}
          <span className="absolute left-1/2 top-[8px] h-[44px] w-[56px] -translate-x-1/2"
            style={{ background: 'linear-gradient(180deg, rgba(168,184,232,0.14), rgba(20,31,61,0.4))', clipPath: 'polygon(0 0, 100% 0, 55% 100%, 45% 100%)', boxShadow: '0 0 0 1.5px rgba(168,184,232,0.3) inset' }} />
          <span className="absolute bottom-[8px] left-1/2 h-[44px] w-[56px] -translate-x-1/2"
            style={{ background: 'linear-gradient(0deg, rgba(168,184,232,0.14), rgba(20,31,61,0.4))', clipPath: 'polygon(45% 0, 55% 0, 100% 100%, 0 100%)', boxShadow: '0 0 0 1.5px rgba(168,184,232,0.3) inset' }} />
          {/* a areia dourada escorre */}
          <motion.span
            aria-hidden
            initial={{ scaleY: 0, opacity: 0 }}
            animate={{ scaleY: 1, opacity: 1 }}
            transition={{ delay: 1.1, duration: 1.2, ease: 'linear' }}
            className="absolute left-1/2 top-[46px] w-[2.5px] origin-top -translate-x-1/2"
            style={{ height: 40, background: 'linear-gradient(180deg, var(--ember-hot), var(--ember))', filter: 'drop-shadow(0 0 5px rgba(246,233,200,0.8))' }}
          />
          {/* montinho que cresce */}
          <motion.span
            aria-hidden
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ delay: 1.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-[10px] left-1/2 h-[14px] w-[34px] origin-bottom -translate-x-1/2 rounded-t-full"
            style={{ background: 'linear-gradient(180deg, var(--ember-hot), var(--ember))' }}
          />
        </motion.div>
        <SceneText
          delay={1.6}
          eyebrow={t('hud.timebackEyebrow', 'a volta no tempo')}
          title={t('hud.timebackTitle', 'A página voltou a ser o que era.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── página limpa: todos os comentários resolvidos ─────────── */
const INK_SPOTS = [
  { x: 22, y: 24, s: 16 }, { x: 62, y: 40, s: 12 }, { x: 34, y: 62, s: 14 }, { x: 70, y: 74, s: 10 },
];

export function CleanPageMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4200);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <motion.div
          initial={{ y: 22, opacity: 0, rotate: -1.5 }}
          animate={{ y: 0, opacity: 1, rotate: -1 }}
          transition={{ type: 'spring', stiffness: 150, damping: 15 }}
          className="relative h-[130px] w-[100px] overflow-hidden rounded-[3px] p-3"
          style={{ background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)', boxShadow: '0 24px 50px rgba(0,0,0,0.6), 0 0 0 1px rgba(70,55,30,0.22)' }}
        >
          {[78, 64, 82, 58, 70].map((w, i) => (
            <span key={i} aria-hidden className="mb-[9px] block h-[3px] rounded-full" style={{ width: `${w}%`, background: 'rgba(69,58,94,0.3)' }} />
          ))}
          {/* as manchas de tinta evaporam, uma a uma */}
          {INK_SPOTS.map((sp, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ opacity: 0.9, scale: 1, y: 0 }}
              animate={{ opacity: 0, scale: 0.4, y: -22 }}
              transition={{ delay: 0.8 + i * 0.28, duration: 0.9, ease: 'easeOut' }}
              className="absolute rounded-full"
              style={{ left: `${sp.x}%`, top: `${sp.y}%`, width: sp.s, height: sp.s, background: 'rgba(138,59,46,0.55)', filter: 'blur(1.5px)' }}
            />
          ))}
          {/* o brilho de limpo varre no fim */}
          <motion.span
            aria-hidden
            initial={{ x: '-130%' }}
            animate={{ x: '130%' }}
            transition={{ delay: 2.1, duration: 0.7, ease: 'easeOut' }}
            className="absolute inset-0"
            style={{ background: 'linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.5) 52%, transparent 64%)' }}
          />
        </motion.div>
        <SceneText
          delay={2.2}
          warm
          eyebrow={t('hud.cleanpageEyebrow', 'revisão concluída')}
          title={t('hud.cleanpageTitle', 'Página limpa.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── a meta cravada: flecha de brasa no alvo ─────────── */
export function GoalSetMoment({ words, onDone }: { words: number; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4200);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative grid h-[130px] w-[130px] place-items-center">
          {/* anéis do alvo */}
          {[52, 36, 20].map((r, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.25 + i * 0.16, type: 'spring', stiffness: 220, damping: 15 }}
              className="absolute rounded-full"
              style={{ width: r * 2, height: r * 2, boxShadow: `0 0 0 1.5px rgba(168,184,232,${0.25 + i * 0.12}) inset` }}
            />
          ))}
          {/* a flecha de brasa crava no centro */}
          <motion.span
            aria-hidden
            initial={{ x: -160, y: -110, rotate: 34, opacity: 0 }}
            animate={{ x: 0, y: 0, rotate: 34, opacity: 1 }}
            transition={{ delay: 0.85, duration: 0.32, ease: [0.6, 0, 0.9, 0.6] }}
            className="absolute h-[3px] w-[62px] origin-right rounded-full"
            style={{ background: 'linear-gradient(90deg, transparent, var(--ember-hot))', filter: 'drop-shadow(0 0 8px rgba(246,233,200,0.9))', right: '50%', top: '50%', marginRight: -2 }}
          />
          <motion.span
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 1.6, 1], opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.5, times: [0, 0.5, 1] }}
            className="absolute h-[10px] w-[10px] rounded-full"
            style={{ background: 'var(--ember-hot)', boxShadow: '0 0 18px 4px rgba(246,233,200,0.8)' }}
            aria-hidden
          />
        </div>
        <SceneText
          delay={1.4}
          warm
          eyebrow={t('hud.goalsetEyebrow', 'a meta está cravada')}
          title={t('hud.goalsetTitle', '{{n}} palavras por noite.', { n: words.toLocaleString('pt-BR') })}
        />
      </div>
    </Scene>
  );
}

/* ─────────── batismo do escritor: um novo título ─────────── */
export function BaptismMoment({ title, onDone }: { title: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4800);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-6 px-6">
        <motion.div
          initial={{ y: 26, opacity: 0, rotate: -1.4 }}
          animate={{ y: 0, opacity: 1, rotate: -1 }}
          transition={{ type: 'spring', stiffness: 150, damping: 15 }}
          className="relative w-[min(320px,86vw)] rounded-[3px_8px_8px_3px] px-7 py-7 text-center"
          style={{ background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)', boxShadow: '0 40px 90px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(70,55,30,0.25)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="font-mono text-[7.5px] uppercase tracking-[0.28em] text-[#A8935F]">
            {t('hud.baptismLabel', 'doravante conhecido como')}
          </p>
          {/* o novo título é caligrafado */}
          <div className="mt-3 overflow-hidden">
            <motion.p
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ delay: 0.6, duration: 1.1, ease: 'linear' }}
              className="mx-auto overflow-hidden whitespace-nowrap font-serif text-[26px] font-medium italic text-[#241C36]"
            >
              {title}
            </motion.p>
          </div>
          <motion.span
            aria-hidden
            initial={{ width: 0 }}
            animate={{ width: '46%' }}
            transition={{ delay: 1.7, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto mt-3 block h-[2px] rounded-full"
            style={{ background: 'linear-gradient(90deg, transparent, var(--ember), transparent)' }}
          />
        </motion.div>
        <SceneText
          delay={2.1}
          warm
          eyebrow={t('hud.baptismEyebrow', 'batismo do escritor')}
          title={t('hud.baptismTitle', 'Suas palavras te deram um novo nome.')}
        />
      </div>
    </Scene>
  );
}

/* ─────────── batismo do livro: o nome gravado na lombada ─────────── */
export function BookNamedMoment({ title, onDone }: { title: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4600);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 150, damping: 16 }}
          className="relative flex h-[190px] w-[54px] items-center justify-center overflow-hidden rounded-[5px] p-2"
          style={{ background: 'linear-gradient(168deg,#1B2A52,#0E1730 55%,#0A1024)', boxShadow: '0 0 0 1px rgba(168,184,232,0.25) inset, 0 24px 48px rgba(0,0,0,0.6)' }}
        >
          {/* o nome sobe gravado em dourado, na vertical */}
          <motion.span
            initial={{ opacity: 0, letterSpacing: '0.6em' }}
            animate={{ opacity: 1, letterSpacing: '0.18em' }}
            transition={{ delay: 0.7, duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
            className="max-h-full overflow-hidden font-serif text-[13px] font-semibold uppercase"
            style={{ writingMode: 'vertical-rl', color: 'var(--ember-hot)', textShadow: '0 0 14px rgba(246,233,200,0.5)' }}
          >
            {title}
          </motion.span>
          <motion.span
            aria-hidden
            initial={{ y: '-130%' }}
            animate={{ y: '130%' }}
            transition={{ delay: 1.8, duration: 0.8, ease: 'easeOut' }}
            className="absolute inset-0"
            style={{ background: 'linear-gradient(180deg, transparent 40%, rgba(255,255,255,0.16) 52%, transparent 64%)' }}
          />
        </motion.div>
        <SceneText
          delay={2}
          warm
          eyebrow={t('hud.booknamedEyebrow', 'batismo do livro')}
          title={t('hud.booknamedTitle', 'A história tem um nome.')}
          sub={title}
        />
      </div>
    </Scene>
  );
}


/* ────────── a fênix: a ofensiva quebrou, a chama renasce das cinzas ────────── */
const PHOENIX_SPARKS = Array.from({ length: 8 }, (_, i) => ({
  x: (i % 2 === 0 ? -1 : 1) * (10 + ((i * 7) % 26)), d: i * 0.12,
}));

export function PhoenixMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4800);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative grid h-[120px] w-[120px] place-items-end justify-items-center">
          <motion.span
            aria-hidden
            initial={{ opacity: 1, scaleY: 1 }}
            animate={{ opacity: 0.35, scaleY: 0.55 }}
            transition={{ delay: 0.9, duration: 1.2 }}
            className="h-[22px] w-[64px] origin-bottom rounded-t-full"
            style={{ background: 'linear-gradient(180deg,#4E5B85,#2A3352)' }}
          />
          <motion.span
            aria-hidden
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 2.6, opacity: 1 }}
            transition={{ delay: 1.1, type: 'spring', stiffness: 160, damping: 11 }}
            className="absolute bottom-[16px]"
            style={{ transformOrigin: 'center bottom' }}
          >
            <span className="flame-loose" />
          </motion.span>
          {PHOENIX_SPARKS.map((sp, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ x: 0, y: 0, opacity: 0 }}
              animate={{ x: sp.x, y: -70 - (i % 3) * 16, opacity: [0, 1, 0] }}
              transition={{ delay: 1.3 + sp.d, duration: 1.2, ease: 'easeOut' }}
              className="absolute bottom-[26px] text-[10px]"
              style={{ color: 'var(--ember-hot)' }}
            >
              ✦
            </motion.span>
          ))}
        </div>
        <SceneText
          delay={1.7}
          warm
          eyebrow={t('hud.phoenixEyebrow', 'a fênix')}
          title={t('hud.phoenixTitle', 'Toda chama pode renascer.')}
          sub={t('hud.phoenixSub', 'a ofensiva recomeça hoje')}
        />
      </div>
    </Scene>
  );
}

/* ────────── a poda: cortar também é escrever ────────── */
const FALLING_LEAVES = [
  { x: 18, r: -30, d: 0 }, { x: 44, r: 20, d: 0.22 }, { x: 66, r: -14, d: 0.44 }, { x: 32, r: 34, d: 0.62 }, { x: 56, r: -24, d: 0.8 },
];

export function PruneMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4400);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-6 px-6">
        <div className="relative h-[130px] w-[160px] overflow-hidden">
          {FALLING_LEAVES.map((lf, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ y: -24, x: 0, rotate: 0, opacity: 0 }}
              animate={{ y: 120, x: [0, 14, -8, 6], rotate: lf.r * 3, opacity: [0, 1, 1, 0] }}
              transition={{ delay: 0.3 + lf.d, duration: 2.1, ease: 'easeInOut' }}
              className="absolute top-0 h-[26px] w-[20px] rounded-[2px]"
              style={{
                left: `${lf.x}%`,
                background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)',
                boxShadow: '0 4px 10px rgba(0,0,0,0.4)',
              }}
            />
          ))}
        </div>
        <SceneText
          delay={1.6}
          eyebrow={t('hud.pruneEyebrow', 'a poda')}
          title={t('hud.pruneTitle', 'Cortar também é escrever.')}
          sub={t('hud.pruneSub', 'a história ficou mais forte')}
        />
      </div>
    </Scene>
  );
}

/* ────────── a alvorada do escritor: meta batida antes do dia ────────── */
export function EarlyBirdMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4600);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative h-[110px] w-[180px] overflow-hidden">
          <span aria-hidden className="absolute inset-x-0 bottom-0 h-px" style={{ background: 'rgba(168,184,232,0.35)' }} />
          <motion.span
            aria-hidden
            initial={{ y: 66, opacity: 0 }}
            animate={{ y: 8, opacity: 1 }}
            transition={{ delay: 0.4, duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-0 left-1/2 h-[64px] w-[64px] -translate-x-1/2 rounded-full"
            style={{ background: 'radial-gradient(circle at 40% 35%,#FFE1AE,#F6E9C8 60%,rgba(223,201,147,0.2))', boxShadow: '0 0 44px rgba(246,233,200,0.6)' }}
          />
          <motion.span
            aria-hidden
            initial={{ opacity: 0, x: -14 }}
            animate={{ opacity: 0.7, x: 10 }}
            transition={{ delay: 1.5, duration: 1.6 }}
            className="absolute left-[18%] top-[16%] text-[13px] text-[rgba(232,229,246,0.7)]"
          >
            ⌄⌄
          </motion.span>
        </div>
        <SceneText
          delay={1.7}
          warm
          eyebrow={t('hud.earlybirdEyebrow', 'a alvorada do escritor')}
          title={t('hud.earlybirdTitle', 'Meta batida antes do dia acordar.')}
        />
      </div>
    </Scene>
  );
}

/* ────────── duas histórias, uma noite ────────── */
export function TwoTalesMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4400);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative h-[120px] w-[190px]">
          {[{ x: 18, r: -5 }, { x: 106, r: 5 }].map((bk, i) => (
            <motion.span
              key={i}
              initial={{ y: 26, opacity: 0, rotate: 0 }}
              animate={{ y: 0, opacity: 1, rotate: bk.r }}
              transition={{ delay: 0.3 + i * 0.24, type: 'spring', stiffness: 170, damping: 15 }}
              className="mcover flex items-end p-2"
              style={{ position: 'absolute', left: bk.x, top: 6, width: 66, height: 100 }}
              aria-hidden
            >
              <span className="mo" />
            </motion.span>
          ))}
          <svg viewBox="0 0 190 120" className="absolute inset-0 h-full w-full" aria-hidden>
            <motion.path
              d="M50 100 C 75 116, 115 116, 140 100"
              fill="none"
              stroke="rgba(223,201,147,0.6)"
              strokeWidth="1.5"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 1.0, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            />
          </svg>
        </div>
        <SceneText
          delay={1.4}
          warm
          eyebrow={t('hud.twotalesEyebrow', 'pena dupla')}
          title={t('hud.twotalesTitle', 'Duas histórias, uma noite.')}
        />
      </div>
    </Scene>
  );
}

/* ────────── três vigílias numa noite ────────── */
export function ThreeVigilsMoment({ n, onDone }: { n: 2 | 3; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4600);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <motion.div
          initial={{ y: 22, opacity: 0, scale: 2 }}
          animate={{ y: 0, opacity: 1, scale: 2 }}
          transition={{ type: 'spring', stiffness: 140, damping: 15 }}
          className="flex items-end gap-4"
          style={{ transformOrigin: 'center bottom' }}
        >
          <span style={{ transform: 'translateY(6px)' }}><BornCandle delay={0.4} /></span>
          <span style={{ transform: 'translateY(-4px)' }}><BornCandle delay={0.8} /></span>
          {n >= 3 && <span style={{ transform: 'translateY(6px)' }}><BornCandle delay={1.2} /></span>}
        </motion.div>
        <SceneText
          delay={1.6}
          warm
          eyebrow={t('hud.threevigilsEyebrow', 'devoção')}
          title={n >= 3
            ? t('hud.threevigilsTitle', 'Três vigílias numa só noite.')
            : t('hud.twovigilsTitle', 'Duas vigílias numa noite.')}
          sub={n >= 3
            ? t('hud.threevigilsSub', 'você voltou, e voltou de novo')
            : t('hud.twovigilsSub', 'você voltou')}
        />
      </div>
    </Scene>
  );
}

/* ────────── o arquivo transborda: 25 almas ────────── */
const FAN_CARDS = [
  { x: -52, r: -16, d: 0 }, { x: -26, r: -8, d: 0.14 }, { x: 0, r: 0, d: 0.28 }, { x: 26, r: 8, d: 0.42 }, { x: 52, r: 16, d: 0.56 },
];

export function ArchiveFullMoment({ count, onDone }: { count: number; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4800);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <div className="relative h-[140px] w-[210px]">
          <motion.span
            aria-hidden
            initial={{ y: 14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 170, damping: 16 }}
            className="absolute bottom-0 left-1/2 h-[46px] w-[150px] -translate-x-1/2 rounded-[6px]"
            style={{ background: 'linear-gradient(180deg,#233052,#141F3D)', boxShadow: '0 0 0 1.5px rgba(168,184,232,0.3) inset, 0 18px 36px rgba(0,0,0,0.55)' }}
          />
          <span aria-hidden className="absolute bottom-[18px] left-1/2 h-[7px] w-[26px] -translate-x-1/2 rounded-full" style={{ background: 'rgba(168,184,232,0.4)' }} />
          {FAN_CARDS.map((fc, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ y: 40, x: 0, rotate: 0, opacity: 0 }}
              animate={{ y: 0, x: fc.x, rotate: fc.r, opacity: 1 }}
              transition={{ delay: 0.5 + fc.d, type: 'spring', stiffness: 200, damping: 16 }}
              className="absolute bottom-[40px] left-1/2 -ml-[27px] h-[74px] w-[54px] rounded-[3px]"
              style={{ background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)', boxShadow: '0 8px 18px rgba(0,0,0,0.45), 0 0 0 1px rgba(70,55,30,0.2)', transformOrigin: 'bottom center' }}
            />
          ))}
        </div>
        <motion.span
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 1.2, type: 'spring', stiffness: 240, damping: 13 }}
          className="-mt-2 font-serif text-[34px] font-semibold"
          style={{ color: 'var(--ember-hot)', textShadow: count >= 100 ? '0 0 22px rgba(246,233,200,0.7)' : undefined }}
        >
          {count}
        </motion.span>
        {count >= 100 && (
          <span aria-hidden className="pointer-events-none -mt-3 flex gap-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: [0, 1, 0.7], scale: 1 }}
                transition={{ delay: 1.5 + i * 0.12, duration: 0.7 }}
                className="text-[11px]"
                style={{ color: 'var(--ember-hot)' }}
              >
                ✦
              </motion.span>
            ))}
          </span>
        )}
        <SceneText
          delay={1.7}
          warm
          eyebrow={t('hud.archiveEyebrow', 'o arquivo transborda')}
          title={t('hud.archiveMilestoneTitle', '{{n}} almas no seu universo.', { n: count })}
        />
      </div>
    </Scene>
  );
}

/* ────────── o pedido à estrela (easter egg da constelação) — micro ────────── */
export function WishMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 2600);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8, transition: { duration: 0.35 } }}
      transition={{ type: 'spring', stiffness: 220, damping: 20 }}
      className="pointer-events-none fixed inset-x-0 bottom-[86px] z-[135] flex justify-center md:bottom-10"
      aria-hidden
    >
      <div className="rounded-full bg-[rgba(13,20,38,0.94)] px-5 py-2.5 shadow-[0_14px_40px_rgba(0,0,0,0.55),0_0_0_1px_rgba(223,201,147,0.3)_inset]">
        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#F6E9C8]">
          ✦ {t('hud.wishTitle', 'pedido feito — agora escreve ele')}
        </span>
      </div>
    </motion.div>
  );
}


/* ────────── o milésimo: mil almas — quase impossível ────────── */
const GALAXY = Array.from({ length: 90 }, (_, i) => {
  const a = i * 2.399963; // ângulo áureo — espiral de galáxia
  const r = 8 + i * 1.35;
  return { x: Math.cos(a) * r, y: Math.sin(a) * r * 0.55, d: (i % 15) * 0.09, s: 1 + (i % 3) * 0.6 };
});

export function ThousandMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 7000);
  return (
    <Scene onDone={onDone} z={146}>
      <div className="flex flex-col items-center gap-8 px-6">
        <motion.div
          aria-hidden
          initial={{ rotate: 0, opacity: 0 }}
          animate={{ rotate: 28, opacity: 1 }}
          transition={{ duration: 6.4, ease: 'linear' }}
          className="relative h-[220px] w-[280px]"
        >
          {GALAXY.map((st, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.9, 0.6] }}
              transition={{ delay: 0.4 + st.d, duration: 1.6 }}
              className="absolute left-1/2 top-1/2 rounded-full"
              style={{
                marginLeft: st.x, marginTop: st.y, width: st.s, height: st.s,
                background: i % 7 === 0 ? '#A78BFA' : '#F6E9C8',
                boxShadow: `0 0 6px 1px ${i % 7 === 0 ? 'rgba(167,139,250,0.7)' : 'rgba(246,233,200,0.7)'}`,
              }}
            />
          ))}
          <motion.span
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 2.2, duration: 1.4 }}
            className="absolute inset-0 grid place-items-center font-serif text-[40px] font-semibold"
            style={{ color: '#EBEEFA', textShadow: '0 0 34px rgba(167,139,250,0.8)' }}
          >
            1000
          </motion.span>
        </motion.div>
        <SceneText
          delay={3}
          eyebrow={t('hud.thousandEyebrow', 'o milésimo')}
          title={t('hud.thousandTitle', 'Mil almas. Um mundo inteiro respira em você.')}
          sub={t('hud.thousandSub', 'nem você conhece todos os nomes')}
        />
      </div>
    </Scene>
  );
}
